import React, {useContext, useLayoutEffect, useState, useEffect} from "react";
import {createUseStyles, useTheme} from "react-jss";
import {Theme} from "engage-client/theme";
import Dashboard from "../dashboard/Dashboard";
import Leaderboard from "../leaderboard/Leaderboard";
import Tabs, {TabPanel} from "engage-client/components/Tabs";
import {PlayerPositionBanner} from "./PlayerPositionBanner";
import {ExpansionTimeMS, LeaderboardRowHeight, LeaderboardRowHeightCompact, PanelState} from "./accessoryConstants";
import {AccessoryTab} from "./AccessoryTab";
import clsx from "clsx";
import {useWindowSize} from "engage-client/hooks/useResizeObserver";
import {EngageAppContext} from "../../App";
import Chat from "../chat/Chat";
import {ChatContext, ChatUIState} from "../../contexts/ChatContext";
import {TriviaStatus} from "../../state/state";
import GameComplete from "../common/GameComplete";


type StyleProps = {
    panelState: PanelState
    windowHeight?: number
    gameComplete: boolean
}

//TODO:Need to figure out how to do multiple computed properties within one class
// @ts-ignore
const useStyles = createUseStyles<Theme>((theme) => ({
    mobilePanel: {
        position: "absolute",
        height: ({panelState:ps, windowHeight}: StyleProps) => ps === PanelState.expanded || ps === PanelState.expanding ? windowHeight : LeaderboardRowHeightCompact + 8,
        [theme.breakpoints.up("sm")] : {
            height: ({panelState:ps, windowHeight}: StyleProps) => ps === PanelState.expanded || ps === PanelState.expanding ? windowHeight : LeaderboardRowHeight + 8,
        },
        left: 0,
        right: 0,
        bottom: 0,
        transition: `height ${ExpansionTimeMS}ms ease-out`,
    },
    playerPositionBanner: {
        position: "absolute",
        boxShadow: "0px 2px 3px #00000071",
        top: 0,
        left: 0,
        right: 0,
        height: LeaderboardRowHeightCompact + 8,
        background: theme.accessoryPanel.tabs.mobileBackground ?? theme.accessoryPanel.tabs.background,
        [theme.breakpoints.up("sm")] : {
            height: LeaderboardRowHeight + 8,
        },
    },
    gameComplete: {
        marginTop: LeaderboardRowHeightCompact + 8,
        [theme.breakpoints.up("sm")] : {
        marginTop: LeaderboardRowHeight + 8,
},
    },
    tabPanelContainer: {
        position: "absolute",
        left: 0,
        right: 0,
        display: "flex",
        flexDirection: "column",
        background: theme.accessoryPanel.background,
        //Using 7 instead of 8 pixels (as used in the banner) so that there's a small overlap, otherwise some of the divs below show through
        top: LeaderboardRowHeightCompact + 7,
        paddingTop: ({gameComplete:gc}: StyleProps) => gc ? 100 : 0,
        height: (props: StyleProps) => (props.windowHeight ?? 0) - (LeaderboardRowHeightCompact + 7),
        [theme.breakpoints.up("sm")] : {
            top: LeaderboardRowHeight + 7,
            height: (props: StyleProps) => (props.windowHeight ?? 0) - (LeaderboardRowHeight + 7),
        },
		borderTop: theme.accessoryPanel.leaderboard.removeBackgroundSVG ? `1px solid ${theme.accessoryPanel.textColor ?? "white"}` : "",
    },
    tabs: {
        background: theme.accessoryPanel.tabs.background,
        borderBottom: "none",
        position: "fixed",
        bottom: 0,
        left: 0,
        right: 0,
        //Start hiding the tabs at the start and near the end of the animation since they stick out above the banner a bit
        opacity: ({panelState:ps}: StyleProps) => ps === PanelState.expanding || ps === PanelState.expanded ? 1 : 0,
        transition: ({panelState:ps}: StyleProps) => `opacity 100ms ease-out ${ps === PanelState.collapsing || ps === PanelState.expanded ? 400 : 0}ms`,
    },
    tabPanelContent: {
        flex: 1,
        display: "flex",
        flexDirection: "column",
        paddingBottom: 36,
        paddingTop: theme.accessoryPanel.leaderboard.removeBackgroundSVG ? 10 : 0,
        "&.leaderboard": {
            alignItems: "flex-end"
        },
        "&.dashboard": {
            overflowY: "scroll",
            alignItems: "stretch",
            "-webkit-overflow-scrolling": "touch"
        },
    },
}));

type MobileAccessoryPanelProps = {
    toggleSlide:()=>void
    panelState: PanelState
    transitionEnd: React.TransitionEventHandler<HTMLDivElement>
    showAnswerWarning: boolean
}

export default function MobileAccessoryPanel({panelState, toggleSlide, transitionEnd, showAnswerWarning}:MobileAccessoryPanelProps) {
    const theme:Theme = useTheme();
    const {appState} = useContext(EngageAppContext.current);
    const {chatReadCount, chatUIState} = useContext(ChatContext);
    const [leaderboardRowCount, setLeaderboardRowCount] = useState(0);
    const [openTab, setOpenTab] = useState(0);
    const {height} = useWindowSize();
    const unreadCount = chatUIState !== ChatUIState.focused ? appState.chat.entries.length - chatReadCount : 0;
    const gameComplete = appState.common.state === TriviaStatus.Complete;
    
    const handleChange = (newTab: number) => {
        setOpenTab(newTab);
    };
    const classes = useStyles({
        windowHeight:height,
        panelState,
        gameComplete,
    });

    useLayoutEffect(()=>{
        document.body.style.overflow = panelState !== PanelState.collapsed ? "hidden" : "visible";
    }, [panelState]);
    
    useEffect(() => {
        if(gameComplete) {
            setOpenTab(1);
        }
    }, [appState.common.state]);

    return <div className={clsx(classes.mobilePanel)} onTransitionEnd={transitionEnd}>
        <PlayerPositionBanner panelState={panelState} toggleSlide={toggleSlide}
                              className={classes.playerPositionBanner} showAnswerWarning={showAnswerWarning}/>
                              
        {gameComplete &&
        <div className={classes.gameComplete}>
            <GameComplete />
        </div>
       }              
        {
            (panelState !== PanelState.collapsed) &&
            <div className={classes.tabPanelContainer}>
                {appState.common.gameOptions.showLeaderboard ?
                    <>
                        <TabPanel value={openTab} index={0}
                                className={clsx(classes.tabPanelContent, "leaderboard")}>
                            <Leaderboard setLeaderboardRowCount={setLeaderboardRowCount}/>
                        </TabPanel>
                        <TabPanel value={openTab} index={1}
                                  className={clsx(classes.tabPanelContent, "dashboard")}>
                            <Dashboard leaderboardRowCount={leaderboardRowCount}/>
                        </TabPanel>
                        {appState.common.gameOptions.showChat &&
                            <TabPanel value={openTab} index={2}
                                      className={clsx(classes.tabPanelContent, "chat")}>
                                <Chat popup={false}/>
                            </TabPanel>
                        }
                        <Tabs value={openTab} onChange={handleChange} className={classes.tabs}>
                            <AccessoryTab label="Leaderboard" icon={theme.accessoryPanel.tabs.leaderboardIcon}/>
                            <AccessoryTab label="Dashboard" icon={theme.accessoryPanel.tabs.dashboardIcon}/>
                            {appState.common.gameOptions.showChat &&
                                <AccessoryTab label="Chat" icon={theme.accessoryPanel.tabs.chatIcon} notificationCount={unreadCount}/>
                            }
                        </Tabs>
                    </> :
                    <Chat popup={false}/>
                }
            </div>
        }
    </div>;
}